import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { useAccount, useWalletClient, usePublicClient, useSwitchChain } from 'wagmi';
import { parseEther, decodeEventLog, decodeAbiParameters, AbiParameter } from 'viem';
import { supabase, STORAGE_BUCKETS, uploadBase64ImageToStorage } from '../../utils/supabase';
import { monad } from '../../utils/chains';

// Monad Brand Colors
const MONAD_PURPLE = '#836EF9';
const MONAD_BLUE = '#200052';
const MONAD_BERRY = '#A0055D';
const MONAD_BLACK = '#0E100F';
const MONAD_OFF_WHITE = '#FBFAF9';

// Monad Token Factory Contract
const MONAD_FACTORY_ADDRESS = '0x09C38615b296023BfC1e6c55f1173d2398047807';
const MONAD_NPM_ADDRESS = '0x7197E214c0b767cFB76Fb734ab638E2c192F4E53';
const MONAD_CHAIN_ID = 143;
const MONADSCAN_API_URL = 'https://api.monadscan.com/api';
const MONADSCAN_API_KEY = (import.meta.env as any).VITE_MONADSCAN_API_KEY || '';

// Factory ABI - matching deployed Monad contract
const FACTORY_ABI = [
  {
    inputs: [
      { internalType: 'string', name: '_name', type: 'string' },
      { internalType: 'string', name: '_symbol', type: 'string' },
      { internalType: 'address', name: 'npm', type: 'address' }
    ],
    name: 'newMavrkToken',
    outputs: [],
    stateMutability: 'payable',
    type: 'function'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'tokenAddress', type: 'address' },
      { indexed: false, internalType: 'string', name: 'name', type: 'string' },
      { indexed: false, internalType: 'string', name: 'symbol', type: 'string' },
      { indexed: true, internalType: 'address', name: 'creator', type: 'address' },
      { indexed: true, internalType: 'address', name: 'npm', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'totalDeployed', type: 'uint256' }
    ],
    name: 'TokenDeployed',
    type: 'event'
  }
] as const;

interface CreateMonadTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #000000;
  border: none;
  border-radius: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  box-sizing: border-box;
  
  @media (max-width: 700px) {
    padding: 24px;
    max-width: 95%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 32px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(90deg);
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${MONAD_OFF_WHITE};
  font-family: 'Inter', sans-serif;
  margin: 0 0 24px 0;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${MONAD_OFF_WHITE};
  font-family: 'Inter', sans-serif;
  margin-bottom: 8px;
  
  .required {
    color: #ff4444;
  }
`;

const Input = styled.input`
  width: 100%;
  background: ${MONAD_BLACK};
  border: 2px solid ${MONAD_PURPLE};
  border-radius: 8px;
  padding: 12px 16px;
  color: ${MONAD_OFF_WHITE};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${MONAD_PURPLE};
    box-shadow: 0 0 0 3px rgba(131, 110, 249, 0.1);
  }

  &::placeholder {
    color: rgba(251, 250, 249, 0.4);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: ${MONAD_BLACK};
  border: 2px solid ${MONAD_PURPLE};
  border-radius: 8px;
  padding: 12px 16px;
  color: ${MONAD_OFF_WHITE};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s ease;
  resize: vertical;
  min-height: 100px;

  &:focus {
    border-color: ${MONAD_PURPLE};
    box-shadow: 0 0 0 3px rgba(131, 110, 249, 0.1);
  }

  &::placeholder {
    color: rgba(251, 250, 249, 0.4);
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: block;
  width: 100%;
  background: rgba(131, 110, 249, 0.1);
  border: 2px dashed ${MONAD_PURPLE};
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: rgba(251, 250, 249, 0.8);
  font-family: 'Inter', sans-serif;
  font-size: 14px;

  &:hover {
    background: rgba(131, 110, 249, 0.2);
    border-color: ${MONAD_PURPLE};
  }
`;

const LogoPreview = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid ${MONAD_PURPLE};
  margin: 12px auto;
  display: block;
`;

const Button = styled.button<{ disabled?: boolean; variant?: 'primary' | 'secondary' }>`
  width: 100%;
  padding: 14px 24px;
  background: ${props => 
    props.disabled 
      ? 'rgba(131, 110, 249, 0.2)' 
      : props.variant === 'secondary'
      ? 'rgba(131, 110, 249, 0.2)'
      : `linear-gradient(135deg, ${MONAD_PURPLE} 0%, ${MONAD_BERRY} 100%)`};
  border: 2px solid ${props => props.disabled ? 'rgba(131, 110, 249, 0.2)' : MONAD_PURPLE};
  border-radius: 10px;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  margin-top: 8px;

  &:hover:not(:disabled) {
    background: ${props => props.variant === 'secondary' 
      ? 'rgba(131, 110, 249, 0.3)' 
      : `linear-gradient(135deg, ${MONAD_PURPLE} 0%, ${MONAD_BERRY} 100%)`};
    opacity: 0.9;
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(131, 110, 249, 0.4);
  }
`;

const StatusMessage = styled.div<{ type: 'info' | 'success' | 'error' }>`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  text-align: center;
  background: ${props => 
    props.type === 'error' ? 'rgba(255, 68, 68, 0.2)' :
    props.type === 'success' ? 'rgba(16, 185, 129, 0.2)' :
    'rgba(131, 110, 249, 0.2)'};
  color: ${props => 
    props.type === 'error' ? '#ff4444' :
    props.type === 'success' ? '#10b981' :
    MONAD_PURPLE};
  border: 1px solid ${props => 
    props.type === 'error' ? '#ff4444' :
    props.type === 'success' ? '#10b981' :
    MONAD_PURPLE};
`;

const SocialsDropdownButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: ${MONAD_BLACK};
  border: 2px solid ${MONAD_PURPLE};
  border-radius: 8px;
  color: ${MONAD_OFF_WHITE};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  margin-bottom: 12px;
  
  &:hover {
    background: rgba(131, 110, 249, 0.1);
    border-color: ${MONAD_OFF_WHITE};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SocialsDropdownContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-bottom: ${props => props.$isOpen ? '20px' : '0'};
`;

const SocialsContainer = styled.div`
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CreateMonadTokenModal: React.FC<CreateMonadTokenModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const { switchChain } = useSwitchChain();
  
  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    description: '',
    website: '',
    twitter: '',
    telegram: '',
    discord: ''
  });
  
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [status, setStatus] = useState<{ type: 'info' | 'success' | 'error'; message: string } | null>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [socialsOpen, setSocialsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper to resize image
  const resizeImage = (file: File, maxSize: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const preview = await resizeImage(file, 512);
      setLogoPreview(preview);
    }
  };

  const handleDeploy = async () => {
    if (!isConnected || !address) {
      setStatus({ type: 'error', message: 'Please connect your wallet first' });
      return;
    }

    if (!walletClient) {
      setStatus({ type: 'error', message: 'Wallet client not available' });
      return;
    }

    if (!formData.name || !formData.symbol) {
      setStatus({ type: 'error', message: 'Name and symbol are required' });
      return;
    }

    if (!logoFile) {
      setStatus({ type: 'error', message: 'Logo is required' });
      return;
    }

    setIsDeploying(true);
    setStatus({ type: 'info', message: 'Switching to Monad network...' });

    try {
      // Switch to Monad chain
      const currentChainId = await walletClient.getChainId();
      if (currentChainId !== MONAD_CHAIN_ID) {
        await switchChain({ chainId: MONAD_CHAIN_ID });
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait for chain switch
      }

      setStatus({ type: 'info', message: 'Deploying token contract...' });

      // Prepare logo for upload
      const logoBase64 = await resizeImage(logoFile, 512);

      // Deploy token using wagmi writeContract
      const hash = await walletClient.writeContract({
        address: MONAD_FACTORY_ADDRESS,
        abi: FACTORY_ABI,
        functionName: 'newMavrkToken',
        args: [formData.name, formData.symbol, MONAD_NPM_ADDRESS],
        value: parseEther('0') // Payable with 0 ETH
      });

      setStatus({ type: 'info', message: 'Waiting for transaction confirmation...' });

      // Wait for transaction receipt
      // Create a provider for Monad chain to ensure we can read receipts
      const { createPublicClient, http } = await import('viem');
      const monadPublicClient = createPublicClient({
        chain: monad,
        transport: http('https://rpc.monad.xyz')
      });
      
      const receipt = await monadPublicClient.waitForTransactionReceipt({ hash });

      if (receipt.status !== 'success') {
        throw new Error('Transaction failed');
      }

      setStatus({ type: 'info', message: 'Parsing transaction logs...' });

      // Parse token address from logs
      let deployedTokenAddress: string | null = null;
      let poolAddress: string | null = null;
      
      for (const log of receipt.logs) {
        try {
          // Skip logs without topics
          if (!log.topics || log.topics.length === 0) {
            continue;
          }
          
          // Check if log is from our factory contract
          if (log.address?.toLowerCase() !== MONAD_FACTORY_ADDRESS.toLowerCase()) {
            continue;
          }
          
          console.log('Parsing log from factory:', {
            address: log.address,
            topics: log.topics,
            dataLength: log.data?.length
          });
          
          // Try to decode TokenDeployed event
          // Event has indexed parameters: tokenAddress, creator, npm (3 topics + signature = 4 topics total)
          // Non-indexed: name, symbol, totalDeployed (in data field)
          const decoded = decodeEventLog({
            abi: FACTORY_ABI,
            data: log.data,
            topics: log.topics as [signature: `0x${string}`, ...args: `0x${string}`[]]
          });
          
          if (decoded.eventName === 'TokenDeployed' && decoded.args) {
            deployedTokenAddress = decoded.args.tokenAddress as string;
            console.log('✅ Token deployed (from receipt):', deployedTokenAddress);
            console.log('Full decoded args:', decoded.args);
            break; // Found it, exit loop
          }
        } catch (e) {
          // Log the error for debugging but continue parsing other logs
          console.warn('Failed to decode log:', e);
          console.warn('Log details:', {
            address: log.address,
            topics: log.topics,
            data: log.data
          });
          
          // Fallback: If this is a log with 4 topics (TokenDeployed has indexed params), try manual extraction
          // TokenDeployed event structure: topics[0]=signature, topics[1]=tokenAddress, topics[2]=creator, topics[3]=npm
          if (log.topics && log.topics.length === 4) {
            try {
              // Extract token address from topics[1] (indexed parameter)
              const tokenAddressTopic = log.topics[1];
              if (tokenAddressTopic && tokenAddressTopic.length === 66) {
                // Topics are 32 bytes, address is last 20 bytes (40 hex chars)
                const tokenAddressHex = tokenAddressTopic.slice(26, 66);
                const potentialAddress = `0x${tokenAddressHex}`;
                
                // Validate it's a valid address format
                if (/^0x[a-fA-F0-9]{40}$/.test(potentialAddress)) {
                  console.log('⚠️ Using fallback manual extraction for token address:', potentialAddress);
                  // Only use this if we haven't found a token address yet
                  if (!deployedTokenAddress) {
                    deployedTokenAddress = potentialAddress.toLowerCase();
                    console.log('✅ Token address extracted manually from topic[1]:', deployedTokenAddress);
                  }
                }
              }
            } catch (manualError) {
              console.warn('Manual extraction also failed:', manualError);
            }
          }
        }
      }

      // Fallback: Try to fetch from Monadscan API if log parsing failed
      if (!deployedTokenAddress) {
        setStatus({ type: 'info', message: 'Log parsing failed, fetching from Monadscan API...' });
        
        try {
          // Fetch transaction receipt from Monadscan API
          const apiResponse = await fetch(
            `${MONADSCAN_API_URL}?module=proxy&action=eth_getTransactionReceipt&txhash=${hash}&apikey=${MONADSCAN_API_KEY}`
          );
          
          if (apiResponse.ok) {
            const apiData = await apiResponse.json();
            
            if (apiData.result && apiData.result.logs) {
              // Parse logs from API response
              for (const log of apiData.result.logs) {
                try {
                  // Check if this log is from our factory contract
                  if (log.address?.toLowerCase() === MONAD_FACTORY_ADDRESS.toLowerCase()) {
                    // Try to decode the event
                    if (log.topics && log.topics.length > 0) {
                      // Calculate event signature hash (keccak256 of "TokenDeployed(address,string,string,address,address)")
                      // TokenDeployed event signature: 0x + keccak256("TokenDeployed(address,string,string,address,address)")
                      const tokenDeployedSignature = '0x9b125304de5c1a87b82cb7735777610731fc602db9764374e5c43ab2a1d26896'; // Pre-calculated
                      
                      // Check if this is a TokenDeployed event
                      // TokenDeployed has 4 topics: [signature, tokenAddress, creator, npm]
                      if (log.topics && log.topics.length === 4) {
                        // Try to decode using the ABI first
                        try {
                          const decoded = decodeEventLog({
                            abi: FACTORY_ABI,
                            data: log.data as `0x${string}`,
                            topics: log.topics as [signature: `0x${string}`, tokenAddress: `0x${string}`, creator: `0x${string}`, npm: `0x${string}`]
                          });
                          
                          if (decoded.eventName === 'TokenDeployed' && decoded.args) {
                            deployedTokenAddress = decoded.args.tokenAddress as string;
                            console.log('Token deployed (from API, ABI decode):', deployedTokenAddress);
                            console.log('Full decoded args:', decoded.args);
                            break; // Found it, exit loop
                          }
                        } catch (decodeError) {
                          // If decodeEventLog fails (signature mismatch), try manual decoding
                          console.warn('decodeEventLog failed, trying manual decode:', decodeError);
                          
                          // Manual decode: Extract token address from topics[1] (indexed parameter)
                          const tokenAddressTopic = log.topics[1];
                          if (tokenAddressTopic && tokenAddressTopic.length === 66) {
                            // Topics are 32 bytes, address is last 20 bytes (40 hex chars)
                            const tokenAddressHex = tokenAddressTopic.slice(26, 66);
                            const potentialAddress = `0x${tokenAddressHex}`;
                            
                            // Validate it's a valid address format
                            if (/^0x[a-fA-F0-9]{40}$/.test(potentialAddress)) {
                              deployedTokenAddress = potentialAddress.toLowerCase();
                              console.log('✅ Token deployed (manual decode from API topic[1]):', deployedTokenAddress);
                              console.log('Event signature:', log.topics[0]);
                              break; // Found it, exit loop
                            }
                          }
                        }
                      }
                    }
                  }
                } catch (e) {
                  console.warn('Failed to parse log from API:', e);
                }
              }
            }
            
            // Alternative: Check for contract creation in the receipt
            if (!deployedTokenAddress && apiData.result.contractAddress) {
              // If the transaction created a contract, it might be the token
              // But we need to verify it's a token contract, not the factory itself
              console.log('Found contract creation:', apiData.result.contractAddress);
            }
          }
        } catch (apiError) {
          console.error('Failed to fetch from Monadscan API:', apiError);
        }
      }
      
      // Final fallback: Try to extract from transaction's internal transactions or contract creation
      if (!deployedTokenAddress) {
        // Try to get contract creation address from the receipt
        // Some factories create tokens via CREATE2, which we can calculate
        // For now, we'll throw an error but provide the transaction hash for manual lookup
        throw new Error(
          `Could not determine token address from transaction logs. ` +
          `Transaction hash: ${hash}. ` +
          `Please check Monadscan: https://www.monadscan.com/tx/${hash} ` +
          `or try fetching manually from the API.`
        );
      }

      setStatus({ type: 'info', message: 'Uploading logo to storage...' });

      // Upload logo to Supabase
      const logoFileName = `${deployedTokenAddress}-logo.png`;
      const logoUrl = await uploadBase64ImageToStorage(
        logoBase64.split(',')[1], // Remove data:image/png;base64, prefix
        STORAGE_BUCKETS.MONAD_LOGOS,
        logoFileName
      );

      if (!logoUrl) {
        throw new Error('Failed to upload logo');
      }

      setStatus({ type: 'info', message: 'Saving token to database...' });

      // Save token to tokens_monad table
      const { error: insertError } = await supabase
        .from('tokens_monad')
        .insert({
          name: formData.name,
          symbol: formData.symbol,
          description: formData.description || null,
          website: formData.website || null,
          twitter: formData.twitter || null,
          telegram: formData.telegram || null,
          discord: formData.discord || null,
          logo_url: logoUrl,
          contract_address: deployedTokenAddress,
          network: 'monad',
          pool_address: poolAddress || null,
          deployer_address: address.toLowerCase()
        });

      if (insertError) {
        throw new Error(`Failed to save token: ${insertError.message}`);
      }

      setStatus({ 
        type: 'success', 
        message: `Token deployed successfully! Address: ${deployedTokenAddress.slice(0, 6)}...${deployedTokenAddress.slice(-4)}` 
      });

      // Reset form
      setFormData({
        name: '',
        symbol: '',
        description: '',
        website: '',
        twitter: '',
        telegram: '',
        discord: ''
      });
      setLogoFile(null);
      setLogoPreview('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Call success callback
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setTimeout(() => {
          onClose();
        }, 2000);
      }

    } catch (error: any) {
      console.error('Deployment error:', error);
      setStatus({ 
        type: 'error', 
        message: error.message || 'Deployment failed. Please try again.' 
      });
    } finally {
      setIsDeploying(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Create Monad Token"
      ariaHideApp={false}
      style={{
        overlay: {
          backgroundColor: 'rgba(0,0,0,0.85)',
          zIndex: 1000,
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        },
        content: {
          maxWidth: '600px',
          margin: 'auto',
          borderRadius: '20px',
          background: 'transparent',
          border: 'none',
          padding: 0,
          inset: 'auto',
          position: 'relative'
        },
      }}
    >
      <ModalContent>
        <CloseButton onClick={onClose}>×</CloseButton>
        <Title>Create Monad Token</Title>

        {status && (
          <StatusMessage type={status.type}>{status.message}</StatusMessage>
        )}

        <FormGroup>
          <Label>
            Token Name <span className="required">*</span>
          </Label>
          <Input
            type="text"
            placeholder="My Token"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled={isDeploying}
          />
        </FormGroup>

        <FormGroup>
          <Label>
            Token Symbol <span className="required">*</span>
          </Label>
          <Input
            type="text"
            placeholder="MTK"
            value={formData.symbol}
            onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
            disabled={isDeploying}
            maxLength={10}
          />
        </FormGroup>

        <FormGroup>
          <Label>Logo <span className="required">*</span></Label>
          <FileInput
            ref={fileInputRef}
            id="logo-upload"
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            disabled={isDeploying}
          />
          <FileInputLabel htmlFor="logo-upload" onClick={(e) => {
            if (!isDeploying) {
              fileInputRef.current?.click();
            }
            e.preventDefault();
          }}>
            {logoPreview ? (
              <>
                <LogoPreview src={logoPreview} alt="Logo preview" />
                <div>Click to change logo</div>
              </>
            ) : (
              'Click to upload logo (PNG, JPG, etc.)'
            )}
          </FileInputLabel>
        </FormGroup>

        <FormGroup>
          <Label>Description (Optional)</Label>
          <TextArea
            placeholder="Describe your token..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            disabled={isDeploying}
            rows={4}
          />
        </FormGroup>

        <FormGroup>
          <SocialsDropdownButton
            type="button"
            onClick={() => setSocialsOpen(!socialsOpen)}
            disabled={isDeploying}
          >
            <span>Social Links (Optional)</span>
            <span>{socialsOpen ? '▼' : '▶'}</span>
          </SocialsDropdownButton>
          <SocialsDropdownContent $isOpen={socialsOpen}>
            <SocialsContainer>
              <FormGroup>
                <Label>Website</Label>
                <Input
                  type="url"
                  placeholder="https://example.com"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  disabled={isDeploying}
                />
              </FormGroup>

              <FormGroup>
                <Label>Twitter</Label>
                <Input
                  type="url"
                  placeholder="https://twitter.com/yourhandle"
                  value={formData.twitter}
                  onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                  disabled={isDeploying}
                />
              </FormGroup>

              <FormGroup>
                <Label>Telegram</Label>
                <Input
                  type="url"
                  placeholder="https://t.me/yourgroup"
                  value={formData.telegram}
                  onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
                  disabled={isDeploying}
                />
              </FormGroup>

              <FormGroup>
                <Label>Discord</Label>
                <Input
                  type="url"
                  placeholder="https://discord.gg/yourserver"
                  value={formData.discord}
                  onChange={(e) => setFormData({ ...formData, discord: e.target.value })}
                  disabled={isDeploying}
                />
              </FormGroup>
            </SocialsContainer>
          </SocialsDropdownContent>
        </FormGroup>

        <Button
          onClick={handleDeploy}
          disabled={isDeploying || !isConnected}
        >
          {isDeploying ? 'Deploying...' : 'Deploy Token'}
        </Button>

        {!isConnected && (
          <StatusMessage type="error">
            Please connect your wallet to deploy a token
          </StatusMessage>
        )}
      </ModalContent>
    </Modal>
  );
};

export default CreateMonadTokenModal;

